import { jourIso } from './rapprochement-sorties';

/**
 * Valeur du stock au prix d'achat, et marge qu'il rapporterait vendu au prix
 * de vente. C'est la formule de l'onglet « Marge » du stock (StockPage),
 * posée ici une fois pour que l'accueil et cet onglet puissent partager le
 * même calcul plutôt que d'en tenir deux qui finiraient par diverger.
 *
 * @param {Array<{stock: number, pa: number, pv: number}>} stock articles
 * @returns {{achat: number, vente: number, margePotentielle: number}}
 */
export function valeurDuStock(stock) {
  const achat = (stock || []).reduce((s, m) => s + (+m.stock || 0) * (+m.pa || 0), 0);
  const vente = (stock || []).reduce((s, m) => s + (+m.stock || 0) * (+m.pv || 0), 0);
  // Le matériel du dépôt sans prix de vente (il part sur les chantiers) ne
  // se vend pas : il ne rendait la marge négative (« CHF -2'127 »).
  const marge = (stock || [])
    .filter((m) => +m.pv > 0)
    .reduce((s, m) => s + (+m.stock || 0) * ((+m.pv || 0) - (+m.pa || 0)), 0);
  return { achat, vente, margePotentielle: marge };
}

/**
 * Les articles à réapprovisionner : il en reste autant ou moins que le seuil
 * choisi par le patron sur chaque article (le « Min. » de la fiche, 5 ou 10
 * pièces par exemple). L'accueil, la cloche et ses notifications comptent les
 * mêmes : une facture du magasin qui fait baisser le stock les fait sonner.
 *
 * @param {Array<{stock: number, min: number}>} stock articles
 * @returns {object[]} les articles au seuil ou en dessous
 */
export function articlesAReapprovisionner(stock) {
  // Sans seuil choisi (Min. vide ou 0), l'article n'alerte pas : un catalogue
  // importé à zéro faisait sonner la cloche pour des centaines d'articles.
  return (stock || []).filter((m) => (+m.min || 0) > 0 && (+m.stock || 0) <= +m.min);
}

/**
 * La marchandise au dépôt : combien d'articles différents, combien de pièces,
 * et ce qu'elles ont coûté.
 *
 * @param {Array<{stock: number, pa: number}>} stock articles
 * @returns {{articles: number, pieces: number, valeur: number}} le dépôt
 */
export function marchandiseAuDepot(stock) {
  const presents = (stock || []).filter((m) => +m.stock > 0);
  return {
    articles: presents.length,
    pieces: presents.reduce((s, m) => s + (+m.stock || 0), 0),
    valeur: valeurDuStock(presents).achat,
  };
}

/**
 * La valeur du stock à une date, au prix d'achat, retrouvée depuis le dépôt
 * d'aujourd'hui : on retire ce qui est entré depuis (achats) et on remet ce
 * qui est sorti depuis (chantiers, ventes ; un retour est une sortie
 * négative). Le patron n'a rien à noter : le journal de chaque article suffit.
 *
 * <p>Un article sans achat noté était déjà là : il compte au début.
 *
 * @param {Array<{stock: number, pa: number, achats?: Array<{date: string, qte: number}>,
 *   sorties?: Array<{date: string, qte: number}>}>} stock articles du dépôt aujourd'hui
 * @param {string} depuis le jour, au format `AAAA-MM-JJ` : ce qui est entré ou
 *   sorti ce jour-là ou après est défait
 * @returns {number} la valeur au prix d'achat
 */
export function stockAuJour(stock, depuis) {
  let total = 0;
  for (const a of stock || []) {
    const apres = (liste) =>
      (liste || [])
        .filter((x) => (jourIso(x?.date) || '9999') >= depuis)
        .reduce((s, x) => s + (+x?.qte || 0), 0);
    const quantite = (+a?.stock || 0) - apres(a?.achats) + apres(a?.sorties);
    total += Math.max(0, quantite) * (+a?.pa || 0);
  }
  return Math.round(total * 100) / 100;
}

/**
 * La valeur du stock au 1er janvier : celle de la clôture de l'année d'avant,
 * sinon celle retrouvée depuis le dépôt et le journal de ses articles
 * ({@link stockAuJour}). Rien à noter dans Compta → Bilan.
 *
 * @param {object} p
 * @param {object} [p.clotures] clôtures par année
 * @param {object[]} [p.stock] articles du dépôt aujourd'hui
 * @param {number} p.annee exercice
 * @returns {number|null} la valeur, ou null sans dépôt connu
 */
export function stockAuDebut({ clotures, stock, annee }) {
  const c = clotures?.[annee - 1];
  if (c && c.stock !== undefined && Number.isFinite(+c.stock)) return +c.stock;
  if (!Array.isArray(stock)) return null;
  return stockAuJour(stock, annee + '-01-01');
}

/**
 * Ce que le stock a gagné sur l'année (fin − début) : la marchandise achetée
 * et pas encore vendue reste au dépôt, elle n'est pas une charge. Le début et
 * la fin d'une année passée viennent de la clôture, sinon du journal du dépôt.
 *
 * @param {object} p
 * @param {object[]} p.stock articles du dépôt aujourd'hui
 * @param {object} [p.clotures] clôtures par année
 * @param {number} p.annee exercice
 * @param {Date} [p.maintenant] aujourd'hui
 * @returns {number|null} la variation, ou null si elle n'est pas connue
 */
export function variationDuStock({ stock, clotures, annee, maintenant = new Date() }) {
  const debut = stockAuDebut({ clotures, stock, annee });
  if (debut === null) return null;
  const fin =
    annee >= maintenant.getFullYear()
      ? valeurDuStock(stock).achat
      : clotures?.[annee]?.stock !== undefined
        ? clotures[annee].stock
        : stockAuDebut({ stock, annee: annee + 1 });
  if (fin === undefined || fin === null || !Number.isFinite(+fin)) return null;
  return Math.round((+fin - debut) * 100) / 100;
}
